"use client";

import Image from "next/image";
import { useCurrentUser } from "queries/current-user";
import type { User } from "queries/current-user/types";
import React from "react";
import { AppleIcon } from "ui/components/icons/Apple";
import { useSession } from "ui/containers/session";
import { useAuth } from "ui/hooks/useAuth";
import { cn } from "ui/lib/utils";

// Imgix team cards under website/coaches/portraits-v2/, the same set the web
// quiz scrolls. Each one already carries the coach, team name and style.
const TEAMS = [
  "forged",
  "define",
  "body_bell",
  "limitless",
  "varsity",
  "era",
  "ignite",
  "transform",
  "ascend",
  "vitality",
  "formation",
  "movewell",
  "flourish",
  "vantage",
  "elevate",
  "endure",
  "versa_2",
  "project_alpha_2",
  "align",
  "evolve",
  "maximus_2",
  "resilient",
  "crews_control",
];

const COLUMN_SECONDS = [70, 90, 80, 100];

const teamCardURL = (key: string) =>
  `https://ladderteams.imgix.net/website/coaches/portraits-v2/${key}.png?w=480&auto=format,compress`;

function TeamWall({ className }: { className?: string }) {
  const columns = COLUMN_SECONDS.map((_, c) =>
    TEAMS.filter((_, i) => i % COLUMN_SECONDS.length === c)
  );

  return (
    <div
      aria-hidden
      className={cn("pointer-events-none overflow-hidden", className)}
    >
      <div className="flex h-full gap-3 px-3 md:gap-4 md:px-4 [mask-image:linear-gradient(to_bottom,black_45%,transparent)] lg:[mask-image:linear-gradient(to_bottom,transparent,black_12%,black_88%,transparent)]">
        {columns.map((teams, c) => (
          <div
            key={c}
            className={cn("min-w-0 flex-1", c === 3 && "hidden xl:block")}
          >
            <div
              className="apollo-team-wall"
              style={{
                animationDuration: `${COLUMN_SECONDS[c]}s`,
                animationDirection: c % 2 ? "reverse" : "normal",
                animationDelay: `-${c * 23}s`,
              }}
            >
              {/* Doubled, with margins instead of gap, so -50% loops seamlessly. */}
              {[...teams, ...teams].map((team, i) => (
                <Image
                  key={`${team}-${i}`}
                  src={teamCardURL(team)}
                  alt=""
                  width={400}
                  height={446}
                  unoptimized
                  priority={i < 2}
                  className="mb-3 block aspect-[400/446] h-auto w-full select-none rounded-[6%/5%] bg-[#181818] md:mb-4"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const capsule =
  "inline-flex h-[54px] w-full items-center justify-center gap-2.5 rounded-full text-[15px] font-semibold transition-[transform,opacity] duration-100 active:scale-[0.97] disabled:opacity-60";

function SignInActions() {
  const { handleLogin, pending } = useAuth();

  return (
    <button
      type="button"
      disabled={pending}
      onClick={() => handleLogin("/programs")}
      className={cn(capsule, "bg-white text-black")}
    >
      <AppleIcon className="-mt-0.5 h-[19px] w-4 [&_path]:fill-black" />
      {pending ? "Opening Apple…" : "Continue with Apple"}
    </button>
  );
}

function WrongAccount({ user }: { user: User }) {
  const { handleLogout } = useAuth();
  const name = [user.givenName, user.familyName].filter(Boolean).join(" ");

  return (
    <>
      <div className="flex items-center gap-3 rounded-[15px] border border-[#333] bg-[#202020] p-3 text-left">
        {user.profileImgImgixURL ? (
          <Image
            src={`${user.profileImgImgixURL}?w=80&h=80&fit=crop`}
            alt=""
            width={40}
            height={40}
            unoptimized
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#333] text-sm font-semibold">
            {(name || user.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">
            {name || "Signed in"}
          </p>
          <p className="truncate text-xs text-[#9e9e9e]">{user.email}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleLogout}
        className={cn(
          capsule,
          "border border-[#333] bg-[#202020] text-white hover:bg-[#2a2a2a]"
        )}
      >
        Sign out and switch account
      </button>
    </>
  );
}

export function SignInScreen() {
  const { session } = useSession();
  const { currentUser, isAdminOrCoach, isLoading } = useCurrentUser();

  // Coaches and admins with a session never reach this page: the proxy sends
  // them on to their program. A session here is a turned-away sign-in.
  const checking = !!session && isLoading;
  const wrongAccount = !!session && !!currentUser && !isAdminOrCoach;

  // Pinned to the app's dark palette in either theme. Theme tokens can't do
  // that here: `.light .theme-ladder` beats a nested `.dark` scope.
  return (
    <div className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#0e0e0e] text-[#fafafa] lg:h-[100dvh] lg:flex-row-reverse">
      <TeamWall className="h-[56dvh] shrink-0 lg:h-full lg:flex-1" />

      <main className="relative z-10 -mt-16 flex flex-1 flex-col px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:mt-0 lg:w-[460px] lg:flex-none lg:justify-between lg:border-r lg:border-[#333] lg:px-12 lg:py-10">
        <div className="hidden items-center gap-2.5 lg:flex">
          <Image
            src="/assets/logo/white-icon.svg"
            alt=""
            width={22}
            height={23}
          />
          <span className="text-[17px] font-semibold">Apollo</span>
        </div>

        <div className="mx-auto flex w-full max-w-[380px] flex-col lg:flex-none">
          <div className="mb-4 flex items-center gap-2.5 lg:hidden">
            <Image
              src="/assets/logo/white-icon.svg"
              alt=""
              width={22}
              height={23}
            />
            <span className="text-[17px] font-semibold">Apollo</span>
          </div>

          <span className="mb-4 w-fit rounded-[4px] border border-[#e6ff00] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#e6ff00]">
            Coaches &amp; admins
          </span>

          <h1 className="text-balance text-[27px] font-semibold leading-[1.15] tracking-tight lg:text-[33px]">
            {wrongAccount
              ? "This account can’t open Apollo"
              : "Run your team from one place."}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-[#9e9e9e]">
            {wrongAccount
              ? "Apollo is for Ladder coaches and admins. Sign out, then continue with the Apple ID on your coach account."
              : "Program the week, build workouts, and keep up with every member on your team."}
          </p>

          <div className="mt-8 flex flex-col gap-3">
            {checking ? (
              <div
                className={cn(
                  capsule,
                  "border border-[#333] bg-[#202020] text-[#9e9e9e]"
                )}
                role="status"
              >
                Checking your account…
              </div>
            ) : wrongAccount ? (
              <WrongAccount user={currentUser} />
            ) : (
              <SignInActions />
            )}
          </div>
        </div>

        <p className="mt-auto pt-10 text-center text-xs text-[#9e9e9e]/80 lg:mt-0 lg:pt-0 lg:text-left">
          Use the Apple ID you sign in to Ladder with.
        </p>
      </main>
    </div>
  );
}
